import { useState } from 'react';
import { useAchievementTypes, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Avatar, Button, ProgressBar } from 'components/common';
import { Box } from 'components/common/box';
import { StatItemWithIcon } from 'components/common/stats';

import type { Achievement } from 'shared-types';
import type { GetCompletionsRes } from 'shared-types/responses';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  numberOfCompletions: GetCompletionsRes['numberOfCompletions'] | undefined;
  numberOfCompletionsByType:
    | GetCompletionsRes['numberOfCompletionsByType']
    | undefined;
}

const AnalyticsCompletionHistoryStats = ({
  numberOfCompletions,
  numberOfCompletionsByType
}: Props) => {
  const {
    messages,
    serverConfig: {
      hideAchievementsFromUI,
      hideCalendarEventsFromUI,
      hideCoursesFromUI,
      hidePathsFromUI,
      individualAchievementsEnabled,
      individualAchievementsDisplayForAdmins
    },
    theme,
    user
  } = useReduxState();

  const [showDetails, setShowDetails] = useState<boolean>(false);

  const achievementTypes = useAchievementTypes();

  const isUserSuperadmin = user?.roles.includes('superadmin');

  const achievementsVisible = !hideAchievementsFromUI;
  const calendarEventsVisible = !hideCalendarEventsFromUI;
  const coursesVisible = !hideCoursesFromUI;
  const pathsVisible = !hidePathsFromUI;

  const showIndividualAchievements =
    individualAchievementsEnabled &&
    achievementsVisible &&
    (isUserSuperadmin || individualAchievementsDisplayForAdmins);

  const completionTypes = [
    ...(coursesVisible
      ? [
          {
            icon: 'book' as IconProp,
            key: 'course',
            label: messages.commonCourse
          }
        ]
      : []),
    ...(pathsVisible
      ? [{ icon: 'tasks' as IconProp, key: 'path', label: messages.commonPath }]
      : []),
    ...(calendarEventsVisible
      ? [
          {
            icon: ['far', 'calendar-days'] as IconProp,
            key: 'calendar-event',
            label: messages.eventCalendarEvent
          }
        ]
      : []),
    ...(achievementsVisible
      ? [
          ...Object.keys(achievementTypes).map(
            (achievementType: Achievement['type']) => ({
              icon: achievementTypes[achievementType].icon,
              key: achievementType,
              label: achievementTypes[achievementType].title
            })
          )
        ]
      : []),
    ...(showIndividualAchievements
      ? [
          {
            icon: 'star' as IconProp,
            key: 'individual-achievement',
            label: messages.individualAchievement
          }
        ]
      : [])
  ]
    .filter((type) => numberOfCompletionsByType?.[type.key])
    .sort((a, b) => {
      const aValue = numberOfCompletionsByType?.[a.key] || 0;
      const bValue = numberOfCompletionsByType?.[b.key] || 0;
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    });

  return (
    <div className="stats">
      <Box padding={0}>
        <div className="header">
          <div className="total">
            <StatItemWithIcon
              icon="check"
              iconBefore={true}
              label={messages.analyticsCompletionsInTotal}
              style={{
                justifyContent: 'normal',
                gap: 15
              }}
              value={numberOfCompletions || 0}
            />
          </div>

          {numberOfCompletions ? (
            <div className="toggle-details">
              <Button
                aria-expanded={showDetails}
                category="lighter"
                icon={showDetails ? 'chevron-up' : 'chevron-down'}
                isRounded={true}
                onClick={() => setShowDetails(!showDetails)}
                size="small"
                text={
                  showDetails
                    ? messages.commonHideDetails
                    : messages.commonShowDetails
                }
              />
            </div>
          ) : null}
        </div>

        {showDetails ? (
          <div className="types">
            {completionTypes.map((type) => (
              <div className="type" key={type.key}>
                <div className="type-icon">
                  <Avatar
                    backgroundColor={theme.lightGreyDark}
                    fontSize="1rem"
                    icon={type.icon}
                    padding="6px 0"
                    size="24px"
                  />
                </div>
                <div className="type-label">{type.label}</div>
                <div className="type-progress">
                  <ProgressBar
                    color={theme.chartMainColor}
                    progress={
                      numberOfCompletions
                        ? (numberOfCompletionsByType?.[type.key] || 0) /
                          numberOfCompletions
                        : 0
                    }
                  />
                </div>
                <div className="type-value">
                  {numberOfCompletionsByType?.[type.key] || 0}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </Box>

      <style jsx>{`
        .stats {
          margin-bottom: 30px;
        }
        .header {
          align-items: center;
          display: flex;
          gap: 20px;
          justify-content: space-between;
          padding: 20px;
        }
        .total {
          flex-grow: 0;
          flex-shrink: 0;
          min-width: 250px;
        }
        .types {
          display: grid;
          flex-grow: 1;
          flex-shrink: 1;
          grid-gap: 1px;
          grid-template-columns: 1fr;
        }
        .type {
          align-items: center;
          box-shadow: 0 0 0 1px ${theme.lightGrey};
          display: flex;
          gap: 10px;
          justify-self: end;
          padding: 10px 15px;
          width: 100%;
        }
        .type-label {
          color: ${theme.textColorLight};
          flex-grow: 1;
          flex-shrink: 1;
          font-size: 1.5rem;
          font-weight: 600;
        }
        .type-progress {
          flex-grow: 0;
          flex-shrink: 0;
          position: relative;
          top: 1px;
          width: 100px;
        }
        .type-value {
          color: ${theme.textColor};
          flex-grow: 0;
          flex-shrink: 0;
          font-size: 1.6rem;
          font-weight: 700;
          text-align: right;
          min-width: 40px;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .types {
            grid-auto-columns: 1fr;
            grid-auto-flow: column;
            grid-template-rows: repeat(
              ${Math.ceil(completionTypes.length / 2)},
              auto
            );
          }
          .types:not(:has(> :nth-child(3))) {
            grid-auto-columns: auto;
            grid-auto-flow: row;
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default AnalyticsCompletionHistoryStats;
